import {
  Chip,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { ProjectListItem } from '@timelog/pm-shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { MaterialSymbol } from '@/components/MaterialSymbol';
import { formatTimestamp } from '@/lib';

/**
 * The portfolio list: one row per project, following TimeLog's Project
 * Portfolio (`Web/src/pages/projects/portfolio/`) in shape rather than in
 * machinery — no filters, no saved views, no search, and deliberately a plain
 * MUI `Table` rather than a data grid, which is not a dependency of this app.
 *
 * Four columns, because four is what this module actually models. The webapp's
 * portfolio also shows health, project manager, account manager and end date;
 * none of them exist on a project here, and inventing them would put fabricated
 * columns in front of the people we are interviewing.
 *
 * A project has no single landing URL (ADR 0031) — the name links to Planning,
 * which is the area a project is worked in first.
 */
export type ProjectTableProps = {
  projects: ProjectListItem[];
  onDelete: (id: string) => void;
};

const HEAD_CELL_SX = {
  bgcolor: 'var(--bg-sunken)',
  color: 'var(--fg-2)',
  borderBottom: 'var(--border-width-thin) solid var(--border-divider)',
  whiteSpace: 'nowrap',
} as const;

export const ProjectTable = ({ projects, onDelete }: ProjectTableProps) => {
  const { t } = useTranslation(['pmPortfolio', 'pmCommon']);
  /*
    One menu for the whole table, anchored to whichever row opened it: a Menu
    per row would mount one Modal per project, and the row that opened it is the
    only thing the menu needs to know.
  */
  const [menu, setMenu] = useState<{ anchor: HTMLElement; project: ProjectListItem } | null>(null);

  return (
    <>
      <Table size="small" data-automation-id="projects-table">
        <TableHead>
          <TableRow>
            <TableCell sx={HEAD_CELL_SX}>{t('table.columns.ProjectName')}</TableCell>
            <TableCell sx={HEAD_CELL_SX}>{t('pmCommon:term.Customer')}</TableCell>
            <TableCell sx={HEAD_CELL_SX}>{t('table.columns.Status')}</TableCell>
            <TableCell sx={HEAD_CELL_SX}>{t('table.columns.LastUpdated')}</TableCell>
            {/* The kebab column: a header label would name the row's actions, not a value. */}
            <TableCell
              sx={{ ...HEAD_CELL_SX, width: 56 }}
              aria-label={t('table.columns.Actions')}
            />
          </TableRow>
        </TableHead>
        <TableBody>
          {projects.map((project) => (
            <TableRow key={project.id} hover data-automation-id="project-row">
              <TableCell>
                <Link
                  component={RouterLink}
                  to={`/projects/${project.id}/planning`}
                  underline="hover"
                  variant="bodyRegular"
                  data-automation-id="project-name-link"
                >
                  {project.name}
                </Link>
              </TableCell>
              <TableCell data-automation-id="project-customer">
                <Typography variant="bodyRegular" sx={{ color: 'var(--fg-2)' }}>
                  {project.customerName ?? '—'}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={t(`status.${project.status}`)}
                  data-automation-id="project-status"
                />
              </TableCell>
              <TableCell data-automation-id="project-updated">
                <Typography variant="bodyRegular" sx={{ color: 'var(--fg-2)' }}>
                  {formatTimestamp(project.updatedAt)}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <IconButton
                  aria-label={t('table.ActionsFor', { name: project.name })}
                  onClick={(event) => setMenu({ anchor: event.currentTarget, project })}
                  data-automation-id="project-actions-button"
                >
                  <MaterialSymbol name="more_vert" size={18} />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Menu
        open={menu !== null}
        anchorEl={menu?.anchor ?? null}
        onClose={() => setMenu(null)}
        data-automation-id="project-actions-menu"
      >
        <MenuItem
          onClick={() => {
            if (menu) onDelete(menu.project.id);
            setMenu(null);
          }}
          data-automation-id="delete-project-button"
        >
          {t('table.actions.Delete')}
        </MenuItem>
      </Menu>
    </>
  );
};
